import { EVENT_TYPE } from '@App/app/entities/shared/event-types.enum';
import { Nullable } from 'babylonjs';
import { ArcRotateCamera } from 'babylonjs';
import { BehaviorSubject, Subscription, interval } from 'rxjs';
import { throttle } from 'rxjs/operators';
import { BroadcastService } from '../../../../shared/broadcast.service';
import { CustomLODService } from '../../custom-lod-service/custom-lod.service';
import { EngineUtilsService } from '../engine-utils-service/engine-utils.service';

/**
 * Shape of the engine context that {@link wireEngineSubscriptions} needs.
 * Implemented by {@link BaseEngineService}; extracted to keep the service file
 * small while preserving its public API.
 */
export interface EngineSubscriptionContext {
  engine: unknown;
  canvas: Nullable<HTMLCanvasElement>;
  camera: Nullable<ArcRotateCamera>;
  broadcastService: BroadcastService;
  utilsService: EngineUtilsService;
  customLODService: CustomLODService;
  isHQModelOnly: boolean;
  _cursorMoveActive$: BehaviorSubject<[boolean, boolean]>;
  _cursorMoveUpdate$: BehaviorSubject<boolean>;
  initSubscribers(): Subscription[];
}

/**
 * Wires the broadcast and cursor-move subscriptions that the engine sets up
 * during its deferred initialisation. Mutates {@link ctx} the same way the
 * original inline constructor logic did.
 */
export const wireEngineSubscriptions = (
  ctx: EngineSubscriptionContext,
  setSpacePressed: (pressed: boolean) => void,
): void => {
  let cursorMoveSubscription: Subscription | null = null;

  ctx.initSubscribers();
  ctx.broadcastService.on(EVENT_TYPE.SPACE_PRESSED).subscribe((pressed: boolean) => {
    setSpacePressed(pressed);
  });
  ctx.broadcastService.on(EVENT_TYPE.CHANGE_CAMERA).subscribe(() => {
    ctx.utilsService.switchCamera(ctx.engine as never, ctx.canvas, ctx.camera);
  });
  ctx._cursorMoveActive$.subscribe(([value, withDebounce]) => {
    if (value && !cursorMoveSubscription) {
      cursorMoveSubscription = ctx._cursorMoveUpdate$
        .pipe(throttle(() => interval(withDebounce ? 100 : 0)))
        .subscribe(() => ctx.utilsService.onCursorMove());
    } else {
      cursorMoveSubscription?.unsubscribe();
      cursorMoveSubscription = null;
    }
  });
  ctx.isHQModelOnly = ctx.customLODService.getFreezed();
};
